import axios from "axios";
import * as Sentry from "@sentry/browser";

/**
 * Custom error type for front-end errors that should be captured in Sentry.
 */
export class SentryFrontendError extends Error {
  public readonly errorCode?: string;

  constructor(
    message: string | undefined,
    options?: {
      errorCode?: string;
      cause?: Error;
      stack?: string;
    }
  ) {
    super(message);
    this.name = "SentryFrontendError";
    this.errorCode = options?.errorCode;
    this.cause = options?.cause;
    this.stack = options?.stack;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SentryFrontendError);
    }
  }
}

/**
 * Handles API related errors and integrates with Axios.
 */
export class APIError extends Error {
  constructor(
    message: string | undefined,
    options?: {
      cause?: Error;
      stack?: string;
      errorCode?: string;
    }
  ) {
    super(message, options);
    this.name = "APIError: " + message;

    // Ensure options is defined before accessing its properties
    if (options?.cause && axios.isAxiosError(options.cause)) {
      this.message = options.cause.message;
    }

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, APIError);
    }
  }
}

/**
 * Represents validation-related client-side issues.
 */
export class ValidationError extends Error {
  constructor(message: string | undefined) {
    super(message);
    this.name = "ValidationError";

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ValidationError);
    }
  }
}

/**
 * Captures any exception and sends it to Sentry.
 */
export function captureSentryException(
  message: string,
  error: unknown,
  errorCode?: string
): void {
  const status = (error as any)?.code || (error as any)?.response?.status;
  const statusCode = errorCode ?? status;

  Sentry.captureException(
    new APIError(message, {
      cause: error instanceof Error ? error : undefined,
      stack: error instanceof Error ? error.stack : undefined,
      errorCode: statusCode,
    }),
    {
      tags: {
        errorType: getErrorSeverity(statusCode),
        statusCode: statusCode,
      },
      level: getErrorLevel(statusCode),
    }
  );
}

/**
 * Determines the category of the error for tagging in Sentry.
 */
function getErrorSeverity(statusCode: string | number): string {
  const code = String(statusCode);

  switch (code) {
    case "404":
      return "routing_issue";
    case "ERR_NETWORK":
      return "network_error";
    default:
      return code.startsWith("4")
        ? "request_error"
        : code.startsWith("5")
        ? "API_error"
        : "unknown_error";
  }
}

/**
 * Determines the logging level of the error in Sentry.
 */
function getErrorLevel(
  statusCode: string | number
): "error" | "warning" | "info" {
  const code = String(statusCode);

  switch (code) {
    case "400":
    case "401":
    case "403":
    case "404":
    case "ERR_NETWORK":
      return "warning";
    case "429":
      return "info";
    case "500":
    case "502":
    case "503":
      return "error";
    default:
      return code.startsWith("5") ? "error" : "warning";
  }
}
